// Platform-operator admin console. Stats across all tenants, KYB queue,
// open disputes, today's FX, audit log preview, recent merchants.
//
// Demo viewer: Demo Admin (scopes: ['*']).

import { Link } from 'react-router-dom';
import {
  Users, Store, Receipt, Banknote, AlertTriangle, FileText,
  LayoutDashboard, TrendingUp, ShieldCheck, Settings, MessageCircle,
  CheckCircle2, RefreshCw, MapPin, ArrowRight,
} from 'lucide-react';
import { PortalShell, type NavSection } from '../components/shared/PortalShell';
import {
  Card, SectionHeader, StatTile, ActivityRow, StatusPill, DemoDataBadge,
} from '../components/shared/primitives';

const ADMIN_NAV: NavSection[] = [
  { items: [
    { id: 'overview',  label: 'Overview',  icon: LayoutDashboard, href: '/admin' },
  ]},
  { title: 'Operations', items: [
    { id: 'users',     label: 'Users',     icon: Users,           href: '/admin/users' },
    { id: 'merchants', label: 'Merchants', icon: Store,           href: '/admin/merchants', badge: 4 },
    { id: 'requests',  label: 'Requests',  icon: Receipt,         href: '/admin/requests' },
    { id: 'payouts',   label: 'Payouts',   icon: Banknote,        href: '/admin/payouts', badge: 7 },
    { id: 'disputes',  label: 'Disputes',  icon: AlertTriangle,   href: '/admin/disputes', badge: 1 },
  ]},
  { title: 'Platform', items: [
    { id: 'fx',        label: 'FX rates',  icon: TrendingUp,      href: '/admin/fx' },
    { id: 'audit',     label: 'Audit log', icon: FileText,        href: '/admin/audit-logs' },
    { id: 'settings',  label: 'Settings',  icon: Settings,        href: '/admin/settings' },
  ]},
];

export function AdminDashboard() {
  return (
    <PortalShell
      nav={ADMIN_NAV}
      activeId="overview"
      title="Platform overview"
      subtitle="VoyeLakay at a glance."
      actions={
        <Link to="/admin/payouts" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-900 text-white text-[12px] font-semibold hover:bg-slate-800">
          <Banknote className="w-3.5 h-3.5" /> Run payouts
        </Link>
      }
    >
      <div className="space-y-6">
        <div className="flex items-center gap-2 -mb-2"><DemoDataBadge note="real numbers ship when Admin queries land in Phase 3" /></div>

        {/* Platform-wide stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatTile icon={Users}        tone="brand"   label="Total users"     value="287"  hint="+12 in last 7 days" trend={{ value: '+4%', positive: true }} />
          <StatTile icon={Receipt}      tone="amber"   label="Open requests"   value="42"   hint="14 awaiting quote · 28 in fulfillment" />
          <StatTile icon={Banknote}     tone="default" label="Pending payouts" value="HTG 184k" hint="7 merchants · ready to batch" />
          <StatTile icon={AlertTriangle} tone="rose"   label="Open disputes"   value="1"    hint="Investigating · 0 SLA breach" />
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 space-y-5">
            {/* KYB queue */}
            <Card>
              <SectionHeader
                title="Merchant KYB queue"
                subtitle="Applications awaiting review."
                actions={<Link to="/admin/merchants" className="text-[12px] font-semibold text-brand-600 hover:underline inline-flex items-center gap-0.5">All merchants <ArrowRight className="w-3 h-3" /></Link>}
              />
              <table className="text-[12.5px] w-full block overflow-x-auto whitespace-nowrap sm:table sm:whitespace-normal">
                <thead className="text-[10.5px] uppercase tracking-wider text-slate-400">
                  <tr>
                    <th className="text-left py-2 font-medium">Business</th>
                    <th className="text-left py-2 font-medium">City</th>
                    <th className="text-left py-2 font-medium">Applied</th>
                    <th className="text-left py-2 font-medium">Status</th>
                    <th className="py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  <KybRow business="Boutique Lakou" city="Léogâne" applied="2h ago" status="applied" />
                  <KybRow business="Pharmacie Anglade" city="Léogâne" applied="6h ago" status="kyb_review" />
                  <KybRow business="Marché Cesselie" city="Léogâne" applied="Yesterday" status="kyb_review" />
                  <KybRow business="Famille Pharmacie" city="Léogâne" applied="2d ago" status="applied" />
                </tbody>
              </table>
            </Card>

            {/* Payout queue preview */}
            <Card>
              <SectionHeader
                title="Payout queue"
                subtitle="Confirmed orders ready to batch."
                actions={<Link to="/admin/payouts" className="text-[12px] font-semibold text-brand-600 hover:underline inline-flex items-center gap-0.5">Batch payouts <ArrowRight className="w-3 h-3" /></Link>}
              />
              <table className="text-[12.5px] w-full block overflow-x-auto whitespace-nowrap sm:table sm:whitespace-normal">
                <thead className="text-[10.5px] uppercase tracking-wider text-slate-400">
                  <tr>
                    <th className="text-left py-2 font-medium">Merchant</th>
                    <th className="text-left py-2 font-medium">Orders</th>
                    <th className="text-left py-2 font-medium">Method</th>
                    <th className="text-right py-2 font-medium">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  <PayoutQueueRow merchant="Marché Demo Léogâne" orders={4} method="MonCash" amount="HTG 9,840" />
                  <PayoutQueueRow merchant="Pharmacie Anglade" orders={6} method="MonCash" amount="HTG 14,720" />
                  <PayoutQueueRow merchant="Boutique Lakou" orders={3} method="Bank" amount="HTG 8,200" />
                  <PayoutQueueRow merchant="Marché Cesselie" orders={5} method="NatCash" amount="HTG 11,400" />
                </tbody>
              </table>
            </Card>
          </div>

          <div className="space-y-5">
            {/* Today's FX */}
            <Card padding="p-4">
              <SectionHeader title="Today's FX" />
              <div className="mt-1 flex items-end justify-between">
                <div>
                  <p className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">USD → HTG</p>
                  <p className="text-[28px] font-semibold tracking-tight text-slate-900 font-mono">132.40</p>
                  <p className="text-[11px] text-slate-500 inline-flex items-center gap-1">
                    <RefreshCw className="w-3 h-3" /> Auto-updated 10 min ago
                  </p>
                </div>
                <span className="text-[11px] font-semibold text-emerald-700 inline-flex items-center gap-0.5">▲ 0.3</span>
              </div>
              <Link to="/admin/fx" className="mt-3 inline-flex items-center gap-1 text-[12px] font-semibold text-brand-600 hover:underline">
                Manage rates →
              </Link>
            </Card>

            {/* Activity */}
            <Card>
              <SectionHeader title="Recent activity" actions={<Link to="/admin/audit-logs" className="text-[12px] font-semibold text-brand-600 hover:underline">Full log →</Link>} />
              <div className="divide-y divide-slate-100 -mx-1 px-1">
                <ActivityRow
                  icon={ShieldCheck} iconTone="emerald"
                  title={<><strong>Demo Admin</strong> approved <strong>Marché Demo Léogâne</strong></>}
                  meta="KYB review · IP 192.0.2.14"
                  time="20m ago"
                />
                <ActivityRow
                  icon={CheckCircle2} iconTone="emerald"
                  title={<>Maria confirmed delivery of <strong>REQ-1037</strong></>}
                  meta="PIN match · payout queued"
                  time="2h ago"
                />
                <ActivityRow
                  icon={MessageCircle} iconTone="brand"
                  title={<>Marie-Claude opened a dispute on <strong>REQ-1018</strong></>}
                  meta="Reason: missing item · 0/3 days SLA"
                  time="4h ago"
                />
                <ActivityRow
                  icon={Banknote} iconTone="slate"
                  title={<>Payout batch <strong>PAY-2026-06-03</strong> marked paid</>}
                  meta="3 merchants · HTG 34,600"
                  time="3d ago"
                />
              </div>
            </Card>

            {/* Recent merchants */}
            <Card>
              <SectionHeader title="Recently active merchants" />
              <div className="space-y-3">
                <MerchantRow business="Marché Demo Léogâne" status="approved" since="Today" orders={4} />
                <MerchantRow business="Pharmacie Anglade" status="kyb_review" since="Yesterday" orders={0} />
                <MerchantRow business="Boutique Lakou" status="applied" since="2h ago" orders={0} />
              </div>
            </Card>
          </div>
        </div>

        <p className="text-[11px] text-slate-400 inline-flex items-center gap-1">
          <FileText className="w-3 h-3" /> All numbers above are demo placeholders. Real data populates when Admin queries land in Phase 3.
        </p>
      </div>
    </PortalShell>
  );
}

function KybRow({ business, city, applied, status }: { business: string; city: string; applied: string; status: string }) {
  return (
    <tr>
      <td className="py-2.5 font-medium text-slate-900">{business}</td>
      <td className="py-2.5 text-slate-600 inline-flex items-center gap-1"><MapPin className="w-3 h-3 text-slate-400" /> {city}</td>
      <td className="py-2.5 text-slate-500">{applied}</td>
      <td className="py-2.5"><StatusPill status={status} /></td>
      <td className="py-2.5 text-right">
        <Link to="/admin/merchants" className="text-[11.5px] font-semibold text-brand-600 hover:underline">Review →</Link>
      </td>
    </tr>
  );
}

function PayoutQueueRow({ merchant, orders, method, amount }: { merchant: string; orders: number; method: string; amount: string }) {
  return (
    <tr>
      <td className="py-2.5 font-medium text-slate-900">{merchant}</td>
      <td className="py-2.5 text-slate-600">{orders} orders</td>
      <td className="py-2.5 text-slate-500">{method}</td>
      <td className="py-2.5 text-right font-mono text-slate-900">{amount}</td>
    </tr>
  );
}

function MerchantRow({ business, status, since, orders }: { business: string; status: string; since: string; orders: number }) {
  return (
    <div className="flex items-center gap-3">
      <span className="inline-flex w-9 h-9 items-center justify-center rounded-lg bg-slate-100 text-slate-600 shrink-0">
        <Store className="w-4 h-4" />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-semibold text-slate-900 truncate">{business}</p>
        <p className="text-[11.5px] text-slate-500">
          {orders > 0 ? `${orders} orders this week · ` : ''}joined {since.toLowerCase()}
        </p>
      </div>
      <StatusPill status={status} />
    </div>
  );
}
